/**
 * One native report (`/reports/<key>`; design §1 "The report page"): breadcrumb header with Save view, the filter bar,
 * KPI strip, charts grid (first chart wide, the rest 2-up, stacked under 900 px), ranked tables, the sources footer, and
 * report-level state banners. Every number drills into the DrillDrawer, which the URL keeps open.
 *
 * A tech who can open Team (Reports → Settings "Techs see their own numbers") sees it as "My numbers" with no owner
 * filter: the API already returns only their row.
 */
import { EmptyState, ErrorBanner } from '@patchbay/ui'
import { useQuery } from '@tanstack/react-query'
import { Link, useSearch } from '@tanstack/react-router'
import { Info } from 'lucide-react'
import * as React from 'react'
import { Frame, PaneHeader } from '../../components/frame'
import type { Drill, ReportResult, SavedViewOut } from '../../lib/api/generated'
import { useMe } from '../auth/session'
import {
  CLEARED_FILTERS,
  CLOSED_DRILL,
  catalogueQuery,
  drillSearch,
  type NativeReportKey,
  type ReportsSearch,
  searchFromFilters,
  suiteReportQuery,
  toApiFilters,
} from './api'
import { ChartCard, chartHidden, KpiStrip, metricVisible, ReportTable } from './charts'
import { DrillDrawer } from './drill-drawer'
import { FilterBar, rangeLabel } from './filter-bar'
import { type CanSee, useCanSee } from './roles'
import { dateLabel, PREPARING, QueryError, sourceLine, whenLabel } from './shared'
import { SaveViewMenu } from './views-menu'

export function Breadcrumb({ title }: { title: React.ReactNode }) {
  const search = useSearch({ strict: false }) as ReportsSearch
  return (
    <span className="flex min-w-0 items-baseline gap-2">
      <Link
        to="/reports"
        search={search.brand ? { brand: search.brand } : {}}
        className="font-semibold text-muted-foreground hover:text-foreground hover:underline"
      >
        Reports
      </Link>
      <span aria-hidden className="text-muted-foreground">
        /
      </span>
      <span className="truncate">{title}</span>
    </span>
  )
}

function ReportBanner({ result }: { result: ReportResult }) {
  const notOk = (result.sources ?? []).filter((s) => s.state !== 'ok')
  if (!result.as_of && result.state === 'unavailable') {
    return <EmptyState title={PREPARING} detail="Once the facts are built, this report fills in. Nothing here is a zero." />
  }
  if (result.state === 'unavailable') {
    return (
      <ErrorBanner
        title="This report couldn't load"
        detail={notOk.length ? notOk.map(sourceLine).join(' · ') : `Facts last built ${whenLabel(result.as_of)}.`}
      />
    )
  }
  if (result.state === 'partial') {
    return (
      <div
        role="status"
        className="flex items-start gap-2 rounded-lg border border-warning/40 bg-warning-soft px-3 py-2 text-sm text-warning"
      >
        <Info className="mt-0.5 size-4 shrink-0" aria-hidden />
        <span>
          Part of this report couldn't load; the rest is shown.{' '}
          {notOk.length ? <span className="text-foreground">{notOk.map(sourceLine).join(' · ')}</span> : null}
        </span>
      </div>
    )
  }
  return null
}

/**
 * What the viewer's role actually sees, with the same visibility rules the components apply (a KPI whose slot was
 * stripped, a chart whose every series is hidden), so "no figures for your role" is never shown over a figure, nor
 * a blank page left without it.
 */
export function visibleFigures(result: ReportResult, canSee: CanSee) {
  return {
    kpis: (result.kpis ?? []).filter((m) => metricVisible(m, canSee)),
    charts: (result.charts ?? []).filter((c) => !chartHidden(c, canSee)),
    tables: result.tables ?? [],
  }
}

function ReportBody({ result, onDrill, updating }: { result: ReportResult; onDrill: (d: Drill) => void; updating?: boolean }) {
  const canSee = useCanSee()
  const { kpis, charts, tables } = visibleFigures(result, canSee)
  const nothing = kpis.length === 0 && charts.length === 0 && tables.length === 0
  return (
    <div className={updating ? 'space-y-5 opacity-60 transition-opacity' : 'space-y-5'} data-updating={updating || undefined}>
      {updating ? (
        <p role="status" className="text-xs text-muted-foreground">
          Updating…
        </p>
      ) : null}
      <ReportBanner result={result} />
      {nothing && result.state === 'ok' ? <EmptyState title="This report has no figures for your role." /> : null}
      {kpis.length ? (
        <section aria-labelledby="report-kpis">
          <h2 id="report-kpis" className="sr-only">
            Headline figures
          </h2>
          <KpiStrip metrics={kpis} onDrill={onDrill} canSee={canSee} />
        </section>
      ) : null}
      {charts.length ? (
        <section aria-labelledby="report-charts">
          <h2 id="report-charts" className="sr-only">
            Charts
          </h2>
          <div className="grid grid-cols-1 gap-4 pane:grid-cols-2">
            {charts.map((chart, i) => (
              <div key={chart.key} className={i === 0 ? 'min-w-0 pane:col-span-2' : 'min-w-0'}>
                <ChartCard chart={chart} onDrill={onDrill} drillReport={result.key} wide={i === 0} canSee={canSee} />
              </div>
            ))}
          </div>
        </section>
      ) : null}
      {tables.length ? (
        <section aria-labelledby="report-tables" className="space-y-4">
          <h2 id="report-tables" className="sr-only">
            Tables
          </h2>
          {tables.map((table) => (
            <ReportTable key={table.key} table={table} onDrill={onDrill} canSee={canSee} />
          ))}
        </section>
      ) : null}
      {(result.sources ?? []).length ? (
        <footer className="border-t border-border pt-3 text-xs text-muted-foreground">
          <h2 className="sr-only">Sources</h2>
          <ul className="flex flex-wrap gap-x-2 gap-y-1">
            {(result.sources ?? []).map((s, i) => (
              <li key={s.name} data-state={s.state} className={s.state === 'ok' ? undefined : 'text-warning'}>
                {i > 0 ? <span aria-hidden>· </span> : null}
                {sourceLine(s)}
              </li>
            ))}
          </ul>
        </footer>
      ) : null}
    </div>
  )
}

const OWN_NUMBERS_HIDDEN = ['owners'] as const

export function SuiteReportScreen({
  reportKey,
  search,
  onSearch,
  onCloseDrill,
}: {
  reportKey: NativeReportKey
  search: ReportsSearch
  onSearch: (patch: Partial<ReportsSearch>) => void
  /** Closes the drill drawer; defaults to clearing the drill from the search. */
  onCloseDrill?: () => void
}) {
  const canSee = useCanSee()
  const role = useMe()?.role
  const ownNumbers = reportKey === 'team' && role === 'tech'
  const catalogue = useQuery(catalogueQuery(search.brand))
  // A tech's Team report is their own row only, so an owner filter (say, from a shared link) means nothing to them.
  const filters = React.useMemo(() => toApiFilters(ownNumbers ? { ...search, owners: undefined } : search), [search, ownNumbers])
  // Keep the previous figures while new filters load, but only for the same report: a different report never shows
  // another report's numbers (the route stays mounted across /reports/<key> changes).
  const report = useQuery({
    ...suiteReportQuery(reportKey, filters),
    placeholderData: (prev, prevQuery) => (prev && prevQuery?.queryKey[2] === reportKey ? prev : undefined),
  })
  const def = catalogue.data?.reports.find((r) => r.key === reportKey)
  const title = ownNumbers ? 'My numbers' : (report.data?.title ?? def?.title ?? reportKey)
  const window = report.data?.window
  const subtitle = window
    ? `${dateLabel(window.from)} to ${dateLabel(window.to)} · by ${window.grain}${
        window.prior_from && window.prior_to ? ` · vs ${dateLabel(window.prior_from)} to ${dateLabel(window.prior_to)}` : ''
      }`
    : (def?.summary ?? rangeLabel(search))
  const onDrill = React.useCallback((d: Drill) => onSearch(drillSearch(d, reportKey)), [onSearch, reportKey])
  const onApply = React.useCallback(
    (view: SavedViewOut) => onSearch({ ...CLEARED_FILTERS, ...CLOSED_DRILL, ...searchFromFilters(view.filters) }),
    [onSearch],
  )

  return (
    <Frame
      active="reports"
      railless
      main={
        <div className="reports-root flex h-full flex-col overflow-y-auto" data-scroll-top>
          <PaneHeader
            title={<Breadcrumb title={title} />}
            subtitle={subtitle}
            actions={<SaveViewMenu report={reportKey} filters={filters} onApply={onApply} />}
          />
          <FilterBar
            search={search}
            onChange={onSearch}
            options={catalogue.data?.filter_options}
            hide={ownNumbers ? OWN_NUMBERS_HIDDEN : undefined}
          />
          <main className="w-full max-w-7xl space-y-4 px-4 py-4 sm:px-5" aria-busy={report.isFetching || undefined}>
            {report.isPending ? (
              <div className="grid grid-cols-2 gap-3 pane:grid-cols-4" role="status" aria-label="Loading report">
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} className="h-24 animate-pulse rounded-lg bg-muted" />
                ))}
              </div>
            ) : report.isError || !report.data ? (
              <QueryError error={report.error} what={def?.title ?? 'This report'} onRetry={() => void report.refetch()} />
            ) : (
              <ReportBody result={report.data} onDrill={onDrill} updating={report.isPlaceholderData} />
            )}
          </main>
          {search.drill ? (
            <DrillDrawer
              report={search.dr ?? reportKey}
              metric={search.drill}
              params={search.dp ?? []}
              filters={filters}
              canSee={canSee}
              onClose={onCloseDrill ?? (() => onSearch(CLOSED_DRILL))}
            />
          ) : null}
        </div>
      }
    />
  )
}
